import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Header } from '../components/Header';
import { PackageCard } from '../components/PackageCard';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Clock, Users, Plus, Minus, Sparkles, Gift, Trash2, Palette, ShieldCheck,
  CalendarDays, User, HeartPulse, CreditCard, ArrowRight, CheckCircle2, PartyPopper, Loader2,
  Pencil, Check, FileText, ChevronRight, CalendarClock, Star,
} from 'lucide-react';
import { toast } from 'sonner';
import { DesignStudioModal } from '../components/DesignStudioModal';
import { StripeCardForm } from '../components/stripe/StripeCardForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TAX_RATE = 0.13;

const CUSTOM_EXTRA_CATALOG = [
  { id: 'helium_balloons', name: 'Helium Balloons', price: 8.99 },
  { id: 'cup_cakes', name: 'Cup Cakes (Assorted)', price: 6.99 },
  { id: 'cheese_cake', name: 'Cheese Cake (Assorted)', price: 7.99 },
  { id: 'brownies', name: 'Brownies', price: 8.99 },
  { id: 'birthday_candles', name: 'Birthday Candles', price: 3.99 },
  { id: 'bobie_the_bear', name: 'Bobie the Bear', price: 18.99 },
  { id: 'plushies', name: 'Plushies', price: 19.99 },
  { id: 'chocolate_pops', name: 'Chocolate Pops', price: 7.99 },
];

const PRINT_GARMENT_MAP = { print_cap: 'cap', print_tshirt: 'tshirt', print_mug: 'mug', byo_print: 'tshirt' };

const ADD_ONS = [
  { id: 'diy_cake_custom', name: 'DIY Cake (Custom) — from $55', price: 55, unit: 'starting price', type: 'toggle' },
  { id: 'extra_30_min', name: 'Extra 30 minutes', price: 50, unit: 'flat', type: 'toggle' },
];
const TOGGLE_ITEM_IDS = new Set(ADD_ONS.map((a) => a.id));

const PREDEFINED_EXTRAS = [
  { id: 'print_cap', name: 'Print a Cap (cap included)', price: 19.0, max: 50 },
  { id: 'print_tshirt', name: 'Print a T-Shirt (shirt included)', price: 15.0, max: 50 },
  { id: 'print_mug', name: 'Print a Mug', price: 15.0, max: 50 },
  { id: 'byo_print', name: 'BYO Cap/T-Shirt — Printing only', price: 5.0, max: 50 },
  { id: 'extra_pizza_slice', name: 'Extra Pizza Slice', price: 2.99, max: 100 },
  { id: 'goodie_bag_upgrade', name: 'Goodie Bag Upgrade', price: 6.0, max: 50 },
  { id: 'gf_df_cupcake', name: 'Gluten-Free / Dairy-Free Cupcake', price: 3.0, max: 50 },
];

const allergyOptions = [
  { id: 'peanuts', label: 'Peanuts' }, { id: 'tree_nuts', label: 'Tree Nuts' },
  { id: 'dairy', label: 'Dairy' }, { id: 'gluten', label: 'Gluten' },
  { id: 'eggs', label: 'Eggs' }, { id: 'soy', label: 'Soy' },
  { id: 'shellfish', label: 'Shellfish' }, { id: 'other', label: 'Other (specify)' },
];
const medicalOptions = [
  { id: 'asthma', label: 'Asthma' }, { id: 'epilepsy', label: 'Epilepsy' },
  { id: 'diabetes', label: 'Diabetes' }, { id: 'adhd', label: 'ADHD' },
  { id: 'autism', label: 'Autism' }, { id: 'other', label: 'Other (specify)' },
];

const MAX_GUESTS = 30;
const SLOT_LABEL = { '10:00': '10:00 AM', '13:00': '1:00 PM', '16:00': '4:00 PM' };
const SS_KEY = 'cb_party_builder_v1';

const LIABILITY_WAIVER_TEXT = [
  {
    h: '1. Assumption of Risk',
    p: 'I understand that participation in a Creative Birthdays party involves arts, crafts, food handling, physical play and the use of materials and equipment, and that these activities carry inherent risks of injury, allergic reaction, illness, or damage to personal property. I voluntarily accept and assume all such risks on behalf of myself, my child, and any guests I am responsible for.',
  },
  {
    h: '2. Allergies & Medical Information',
    p: 'I confirm that the allergy and medical information I have provided is accurate and complete. Creative Birthdays will take reasonable care but cannot guarantee an allergen-free environment. I am responsible for informing staff of any condition that may affect my child or guests and for providing any required medication (e.g. EpiPen).',
  },
  {
    h: '3. Supervision',
    p: 'A parent or designated guardian must remain on the premises and is responsible for the general supervision and conduct of the birthday child and attending guests throughout the event.',
  },
  {
    h: '4. Release of Liability',
    p: 'To the fullest extent permitted by law, I release and hold harmless Creative Birthdays, its owners, employees and contractors from any and all claims, demands, or causes of action arising out of ordinary negligence in connection with the party, except for injury or loss caused by gross negligence or wilful misconduct.',
  },
  {
    h: '5. Photography & Media',
    p: 'Photography/video consent is handled separately above and is entirely optional. Declining does not affect your booking.',
  },
  {
    h: '6. Cancellation & Deposit',
    p: 'A 50% deposit is required to hold your date. You may cancel with a full refund up to 24 hours before your party; cancellations within 24 hours are non-refundable. Date changes are subject to availability.',
  },
  {
    h: '7. Payment',
    p: 'The remaining balance is due on or before the day of the party. I authorise Creative Birthdays to charge the payment method provided for the amounts shown at checkout.',
  },
];

// Compact add-on card (mirrors the redesigned add-ons UI)
const AddonItemCard = ({ item, isToggle, toggleValue, qtyValue, onToggle, onQtyChange }) => {
  const selected = isToggle ? !!toggleValue : qtyValue > 0;
  const max = Number(item.max_qty) || 50;
  const priceLabel = item.unit_label === 'flat' ? 'flat' : item.unit_label || 'each';
  return (
    <div
      className={`rounded-xl border-2 transition-all p-2 flex flex-col gap-1.5 ${
        selected ? 'border-[#FF3B6B] bg-[#FFEDF3] shadow-sm' : 'border-[#E8E5DF] bg-white hover:border-[#FF6B6B]/40'
      }`}
      data-testid={`addon-card-${item.item_id}`}
    >
      <div className="w-full aspect-square rounded-lg overflow-hidden bg-[#FAF8F3] flex items-center justify-center">
        {item.image_url ? (
          <img src={`${API_URL}${item.image_url}`} alt={item.name} className="w-full h-full object-cover" loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (<span className="text-3xl">🎉</span>)}
      </div>
      <div className="flex-1 min-h-[28px]">
        <p className="text-xs font-semibold text-[#1E1E24] leading-tight line-clamp-2">{item.name}</p>
      </div>
      <div className="flex items-center justify-between gap-1">
        <p className="text-xs font-bold text-[#FF3B6B] tabular-nums leading-none">
          ${Number(item.price || 0).toFixed(2)}
          <span className="block text-[9px] text-[#6B6B7A] font-normal">{priceLabel}</span>
        </p>
        {isToggle ? (
          <button type="button" onClick={() => onToggle(!toggleValue)}
            className={`px-2 h-7 rounded-full text-[11px] font-semibold transition-colors whitespace-nowrap ${
              toggleValue ? 'bg-[#FF3B6B] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:border-[#FF3B6B]'}`}
            data-testid={`addon-toggle-${item.item_id}`}>
            {toggleValue ? '✓' : '+ Add'}
          </button>
        ) : (
          <div className="flex items-center gap-0.5 bg-white rounded-full border border-[#E8E5DF] px-0.5 py-0.5">
            <button type="button" onClick={() => onQtyChange(Math.max(0, qtyValue - 1))} disabled={qtyValue <= 0}
              className="w-6 h-6 rounded-full flex items-center justify-center text-[#FF3B6B] disabled:text-[#D9D5CC] hover:bg-[#FFEDF3]"
              data-testid={`addon-minus-${item.item_id}`}><Minus className="w-3 h-3" /></button>
            <span className="text-xs font-bold tabular-nums w-5 text-center" data-testid={`addon-qty-${item.item_id}`}>{qtyValue}</span>
            <button type="button" onClick={() => onQtyChange(Math.min(max, qtyValue + 1))} disabled={qtyValue >= max}
              className="w-6 h-6 rounded-full flex items-center justify-center text-[#FF3B6B] disabled:text-[#D9D5CC] hover:bg-[#FFEDF3]"
              data-testid={`addon-plus-${item.item_id}`}><Plus className="w-3 h-3" /></button>
          </div>
        )}
      </div>
    </div>
  );
};

export const PartyBuilder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [packages, setPackages] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);

  // Build selections
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [guestCount, setGuestCount] = useState(8);
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [extrasQty, setExtrasQty] = useState({});
  const [customExtras, setCustomExtras] = useState([]);
  const [showCustomExtraForm, setShowCustomExtraForm] = useState(false);
  const [customExtraDraft, setCustomExtraDraft] = useState({ name: '', unit_price: '', quantity: 1 });
  const [customChargeDesc, setCustomChargeDesc] = useState('');
  const [customChargeAmount, setCustomChargeAmount] = useState('');
  const [designs, setDesigns] = useState([]);
  const [designOpen, setDesignOpen] = useState(false);
  const [designInitialGarment, setDesignInitialGarment] = useState('tshirt');

  // Discounts
  const [promoInput, setPromoInput] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [giftCardInput, setGiftCardInput] = useState('');
  const [giftCardValidating, setGiftCardValidating] = useState(false);
  const [appliedGiftCard, setAppliedGiftCard] = useState(null);
  const [giftCardError, setGiftCardError] = useState('');
  const [loyaltyVouchers, setLoyaltyVouchers] = useState([]);
  const [appliedFreebieVoucher, setAppliedFreebieVoucher] = useState(null);
  const [referralInput, setReferralInput] = useState('');
  const [referralValidating, setReferralValidating] = useState(false);
  const [appliedReferral, setAppliedReferral] = useState(null);
  const [referralError, setReferralError] = useState('');
  const [activeOffers, setActiveOffers] = useState([]);

  // Stepper (auto-scroll + collapse)
  const [packageCollapsed, setPackageCollapsed] = useState(false);
  const [guestsCollapsed, setGuestsCollapsed] = useState(false);
  const [activeCat, setActiveCat] = useState(null);

  // Liability waiver consent gate
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [waiverRead, setWaiverRead] = useState(false);

  // Checkout (revealed after "Hold Date")
  const [showCheckout, setShowCheckout] = useState(false);
  // Which primary action the customer chose: '50% deposit now' vs '24h pay-later hold'
  const [holdMode, setHoldMode] = useState('deposit');
  const [payLaterAck, setPayLaterAck] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [childName, setChildName] = useState('');
  const [childAge, setChildAge] = useState('');
  const [edibleNotes, setEdibleNotes] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');

  // Allergy & consent
  const [allergies, setAllergies] = useState([]);
  const [allergyOther, setAllergyOther] = useState('');
  const [medicalConditions, setMedicalConditions] = useState([]);
  const [medicalOther, setMedicalOther] = useState('');
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [emergencyRelationship, setEmergencyRelationship] = useState('');
  const [photoConsent, setPhotoConsent] = useState(false);
  const [liabilityAccepted, setLiabilityAccepted] = useState(false);
  const [signatureName, setSignatureName] = useState('');

  // Payment
  const [submitting, setSubmitting] = useState(false);
  const [createdBooking, setCreatedBooking] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [loyaltyRate, setLoyaltyRate] = useState(null);

  const checkoutRef = useRef(null);
  const paymentRef = useRef(null);
  const guestsRef = useRef(null);
  const addonsRef = useRef(null);
  const restoredRef = useRef(false);

  useEffect(() => {
    fetchPackages();
    fetchTimeSlots();
    fetchAddonsCatalog();
    fetchLoyaltyVouchers();
    fetchActiveOffers();
    fetchPaypalConfig();
    fetchLoyaltyRate();
  }, []);

  useEffect(() => { if (selectedDate) fetchAvailableSlots(selectedDate); }, [selectedDate]);

  const fetchPackages = async () => {
    try { const r = await axios.get(`${API_URL}/api/packages`); setPackages(r.data); }
    catch { toast.error('Failed to load packages'); }
    finally { setLoading(false); }
  };
  const fetchTimeSlots = async () => { try { const r = await axios.get(`${API_URL}/api/time-slots`); setTimeSlots(r.data); } catch { /* noop */ } };
  const fetchAddonsCatalog = async () => {
    setCatalogLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/addons/catalog`);
      const data = r.data || { categories: [] };
      setCatalog(data);
      const firstCat = (data.categories || []).find((c) => (c.items || []).length > 0);
      if (firstCat) setActiveCat(firstCat.category_id);
    } catch { setCatalog({ categories: [] }); }
    finally { setCatalogLoading(false); }
  };
  const fetchLoyaltyVouchers = async () => {
    try { const r = await axios.get(`${API_URL}/api/customer/loyalty`, { withCredentials: true }); setLoyaltyVouchers(r.data?.active_vouchers || []); } catch { /* not logged in */ }
  };
  const fetchActiveOffers = async () => {
    try { const r = await axios.get(`${API_URL}/api/special-offers/active`); setActiveOffers((r.data || []).filter((o) => !o.promo_code)); } catch { setActiveOffers([]); }
  };
  const fetchPaypalConfig = async () => {
    try { const r = await axios.get(`${API_URL}/api/payments/paypal/client-id`); setPaypalConfig(r.data); } catch { setPaypalConfig(null); }
  };
  const fetchLoyaltyRate = async () => {
    try { const r = await axios.get(`${API_URL}/api/public/loyalty/active-rate`); setLoyaltyRate(r.data); } catch { setLoyaltyRate(null); }
  };
  const fetchAvailableSlots = async (date) => {
    try {
      const dateStr = date.toISOString().split('T')[0];
      const r = await axios.get(`${API_URL}/api/bookings/available-slots/${dateStr}`);
      const avail = r.data.available_slots || [];
      setAvailableSlots(avail);
      setSelectedTime((prev) => (prev && avail.includes(prev) ? prev : ''));
    } catch { /* noop */ }
  };

  const offerForPackage = (pkg) => {
    if (!pkg || activeOffers.length === 0) return null;
    let best = null; let bestAmount = 0;
    for (const o of activeOffers) {
      if (o.applies_to !== 'all' && o.applies_to !== pkg.id) continue;
      const amt = o.discount_type === 'percent'
        ? (Number(pkg.price || 0) * Number(o.discount_value || 0)) / 100
        : Math.min(Number(o.discount_value || 0), Number(pkg.price || 0));
      if (amt > bestAmount) { bestAmount = amt; best = { ...o, amount: Math.round(amt * 100) / 100 }; }
    }
    return best;
  };

  // ── sessionStorage persistence ──────────────────────────────────────────
  useEffect(() => {
    // restore once, after packages load
    if (restoredRef.current || packages.length === 0) return;
    restoredRef.current = true;
    try {
      const raw = sessionStorage.getItem(SS_KEY);
      if (!raw) { pickDefaultFromQuery(); return; }
      const s = JSON.parse(raw);
      const pkg = packages.find((p) => p.id === s.packageId);
      if (pkg) { setSelectedPackage(pkg); setGuestCount(s.guestCount || pkg.guests || 8); }
      setSelectedAddOns(s.selectedAddOns || {});
      setExtrasQty(s.extrasQty || {});
      setCustomExtras(s.customExtras || []);
      setChildName(s.childName || ''); setChildAge(s.childAge || '');
      setCustomerName(s.customerName || ''); setCustomerEmail(s.customerEmail || '');
      setCustomerPhone(s.customerPhone || ''); setCustomerAddress(s.customerAddress || '');
      setEdibleNotes(s.edibleNotes || '');
    } catch { pickDefaultFromQuery(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packages]);

  const pickDefaultFromQuery = () => {
    const qp = searchParams.get('package');
    if (qp) { const p = packages.find((x) => x.id === qp); if (p) selectPackage(p); }
  };

  useEffect(() => {
    if (!restoredRef.current) return;
    try {
      sessionStorage.setItem(SS_KEY, JSON.stringify({
        packageId: selectedPackage?.id || null, guestCount,
        selectedAddOns, extrasQty, customExtras,
        childName, childAge, customerName, customerEmail, customerPhone, customerAddress, edibleNotes,
      }));
    } catch { /* quota */ }
  }, [selectedPackage, guestCount, selectedAddOns, extrasQty, customExtras, childName, childAge, customerName, customerEmail, customerPhone, customerAddress, edibleNotes]);

  const selectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setGuestCount(pkg.guests || 8);
    setPackageCollapsed(true);
    setGuestsCollapsed(false);
    scrollTo(guestsRef);
  };

  const isDateDisabled = (date) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const maxDate = new Date(); maxDate.setDate(maxDate.getDate() + 90);
    return date < today || date > maxDate;
  };

  // ── Pricing ───────────────────────────────────────────────────────────────
  const pricing = useMemo(() => {
    if (!selectedPackage) return { subtotal: 0, tax: 0, total: 0, deposit: 0, addOnsTotal: 0, extrasTotal: 0, customExtrasTotal: 0, discountAmount: 0, preDiscountSubtotal: 0, freebieAmount: 0, specialOfferAmount: 0, specialOffer: null, giftCardAmount: 0, extraGuestCount: 0, extraGuestTotal: 0 };
    let subtotal = selectedPackage.price;
    const included = selectedPackage.guests || 0;
    const extraGuestPrice = selectedPackage.extra_guest_price || 0;
    const extraGuestCount = Math.max(0, guestCount - included);
    const extraGuestTotal = Math.round(extraGuestCount * extraGuestPrice * 100) / 100;
    subtotal += extraGuestTotal;

    let addOnsTotal = 0;
    ADD_ONS.forEach((a) => { if (selectedAddOns[a.id]) addOnsTotal += a.price; });
    // catalog toggle items live in selectedAddOns too
    const catalogItems = (catalog?.categories || []).flatMap((c) => c.items || []);
    catalogItems.forEach((it) => {
      if (TOGGLE_ITEM_IDS.has(it.item_id)) { if (selectedAddOns[it.item_id]) addOnsTotal += Number(it.price) || 0; }
    });
    // avoid double count: ADD_ONS ids equal catalog toggle ids → subtract dup
    ADD_ONS.forEach((a) => { const inCat = catalogItems.find((it) => it.item_id === a.id); if (inCat && selectedAddOns[a.id]) addOnsTotal -= a.price; });
    subtotal += addOnsTotal;

    let extrasTotal = 0;
    const qtyItems = catalogItems.filter((it) => !TOGGLE_ITEM_IDS.has(it.item_id));
    if (qtyItems.length > 0) { qtyItems.forEach((it) => { const q = extrasQty[it.item_id] || 0; if (q > 0) extrasTotal += Number(it.price) * q; }); }
    else { PREDEFINED_EXTRAS.forEach((it) => { const q = extrasQty[it.id] || 0; if (q > 0) extrasTotal += it.price * q; }); }
    subtotal += extrasTotal;

    const customExtrasTotal = customExtras.reduce((s, e) => { const p = parseFloat(e.unit_price) || 0; const q = parseInt(e.quantity, 10) || 0; return s + (p > 0 && q > 0 ? p * q : 0); }, 0);
    subtotal += customExtrasTotal;
    const customAmount = parseFloat(customChargeAmount) || 0;
    subtotal += customAmount;

    const preDiscountSubtotal = subtotal;
    let specialOfferAmount = 0; let specialOffer = null;
    if (!appliedPromo && preDiscountSubtotal > 0) {
      const off = offerForPackage(selectedPackage);
      if (off) {
        const amt = off.discount_type === 'percent' ? Math.round(preDiscountSubtotal * (Number(off.discount_value) / 100) * 100) / 100 : Math.min(Number(off.discount_value), preDiscountSubtotal);
        if (amt > 0) { specialOfferAmount = amt; specialOffer = { ...off, amount: amt }; subtotal = Math.max(0, preDiscountSubtotal - amt); }
      }
    }
    let discountAmount = 0;
    if (appliedPromo && preDiscountSubtotal > 0) {
      discountAmount = appliedPromo.type === 'percent' ? Math.round(preDiscountSubtotal * (appliedPromo.value / 100) * 100) / 100 : Math.min(appliedPromo.value, preDiscountSubtotal);
      subtotal = Math.max(0, preDiscountSubtotal - discountAmount);
    }
    let freebieAmount = 0;
    if (appliedFreebieVoucher) {
      const fid = appliedFreebieVoucher.freebie_extra_id;
      const pre = PREDEFINED_EXTRAS.find((x) => x.id === fid);
      const cust = CUSTOM_EXTRA_CATALOG.find((x) => x.id === fid);
      const inPredefined = pre && (extrasQty[fid] || 0) >= 1;
      const inCustom = cust && customExtras.some((e) => e.catalog_id === fid && (parseInt(e.quantity, 10) || 0) >= 1);
      if (inPredefined || inCustom) { freebieAmount = Math.min(appliedFreebieVoucher.unit_price || pre?.price || cust?.price || 0, subtotal); subtotal = Math.max(0, Math.round((subtotal - freebieAmount) * 100) / 100); }
    }
    let giftCardAmount = 0;
    if (appliedGiftCard && subtotal > 0) { giftCardAmount = Math.round(Math.min(appliedGiftCard.balance_remaining || 0, subtotal) * 100) / 100; subtotal = Math.max(0, Math.round((subtotal - giftCardAmount) * 100) / 100); }

    const tax = subtotal * TAX_RATE;
    const total = subtotal + tax;
    const deposit = total * 0.5;
    return { subtotal, tax, total, deposit, addOnsTotal, extrasTotal, customExtrasTotal, discountAmount, preDiscountSubtotal, freebieAmount, specialOfferAmount, specialOffer, giftCardAmount, extraGuestCount, extraGuestTotal };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPackage, guestCount, selectedAddOns, extrasQty, customExtras, customChargeAmount, appliedPromo, appliedFreebieVoucher, appliedGiftCard, catalog, activeOffers]);

  // ── Discount validators ─────────────────────────────────────────────────
  const validatePromo = async (codeArg) => {
    const code = ((codeArg ?? promoInput) || '').trim().toUpperCase();
    if (!code) { setPromoError('Enter a code first'); return; }
    setPromoValidating(true); setPromoError('');
    try {
      const res = await axios.post(`${API_URL}/api/discounts/validate`, { code, customer_email: customerEmail || undefined, subtotal: 100 });
      if (res.data?.valid) { setAppliedPromo({ code: res.data.code, type: res.data.type, value: res.data.value, description: res.data.description }); toast.success(`Promo ${res.data.code} applied`); }
      else { setAppliedPromo(null); setPromoError(res.data?.reason || 'Invalid code'); }
    } catch (e) { setAppliedPromo(null); setPromoError(e.response?.data?.detail || 'Could not validate code'); }
    finally { setPromoValidating(false); }
  };
  const validateGiftCard = async (codeArg) => {
    const code = ((codeArg ?? giftCardInput) || '').trim().toUpperCase();
    if (!code) { setGiftCardError('Enter a code first'); return; }
    setGiftCardValidating(true); setGiftCardError('');
    try {
      const res = await axios.post(`${API_URL}/api/gift-cards/validate`, { code });
      if (res.data?.valid) { setAppliedGiftCard({ code: res.data.code, amount: res.data.amount, balance_remaining: res.data.balance_remaining, expires_at: res.data.expires_at }); toast.success(`Gift card ${res.data.code} applied`); }
      else { setAppliedGiftCard(null); setGiftCardError('Invalid code'); }
    } catch (e) { setAppliedGiftCard(null); setGiftCardError(e.response?.data?.detail || 'Could not validate code'); }
    finally { setGiftCardValidating(false); }
  };
  const validateReferral = async (codeArg) => {
    const code = ((codeArg ?? referralInput) || '').trim().toUpperCase();
    if (!code) { setReferralError('Enter a code first'); return; }
    setReferralValidating(true); setReferralError('');
    try {
      const res = await axios.post(`${API_URL}/api/public/referral/validate`, { code, customer_email: customerEmail || undefined });
      if (res.data?.valid) { setAppliedReferral({ code, referrer_first_name: res.data.referrer_first_name }); toast.success(`Referred by ${res.data.referrer_first_name}!`); }
      else { setAppliedReferral(null); setReferralError(res.data?.reason || 'Invalid code'); }
    } catch (e) { setAppliedReferral(null); setReferralError(e.response?.data?.detail || 'Could not validate code'); }
    finally { setReferralValidating(false); }
  };
  const applyDiscountVoucher = (v) => { if (v?.discount_code) { setPromoInput(v.discount_code); validatePromo(v.discount_code); } };
  const applyFreebieVoucher = (v) => {
    const fid = v?.freebie_extra_id; if (!fid) return;
    const pre = PREDEFINED_EXTRAS.find((x) => x.id === fid);
    if (pre) setExtrasQty((prev) => ({ ...prev, [fid]: Math.max(prev[fid] || 0, 1) }));
    setAppliedFreebieVoucher({ voucher_id: v.voucher_id, reward_name: v.reward_name, freebie_extra_id: fid, unit_price: pre?.price || 0 });
    toast.success(`"${v.reward_name}" will be applied`);
  };

  // ── Design + custom extras ─────────────────────────────────────────────
  const openDesignStudio = (garment = 'tshirt') => { setDesignInitialGarment(garment); setDesignOpen(true); };
  const onDesignSaved = (design) => { setDesigns((p) => [...p, { ...design, id: `design_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` }]); toast.success('Design saved'); };
  const removeDesign = (id) => setDesigns((p) => p.filter((d) => d.id !== id));
  const toggleAllergy = (id) => setAllergies((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleMedical = (id) => setMedicalConditions((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const scrollTo = (ref) => setTimeout(() => { try { ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { /* noop */ } }, 80);

  const handleHold = (mode = 'deposit') => {
    if (!selectedPackage) { toast.error('Please select a package first'); return; }
    setHoldMode(mode);
    setShowCheckout(true);
    scrollTo(checkoutRef);
  };

  // ── Shared checkout validation + booking creation ──────────────────────
  const validateCheckout = () => {
    if (!selectedPackage) { toast.error('Select a package'); return false; }
    if (!selectedDate || !selectedTime) { toast.error('Pick a date and time'); return false; }
    if (!childName || !childAge) { toast.error('Enter the birthday child\u2019s name and age'); return false; }
    if (guestCount < 1) { toast.error('Guest count must be at least 1'); return false; }
    if (!customerName || !customerEmail || !customerPhone) { toast.error('Fill in your contact details'); return false; }
    if (!emergencyName || !emergencyPhone || !emergencyRelationship) { toast.error('Emergency contact is required'); return false; }
    if (!liabilityAccepted) { toast.error('Please accept the liability waiver'); return false; }
    if (!signatureName.trim()) { toast.error('Please sign (type your name)'); return false; }
    return true;
  };

  const createBookingWithAllergy = async () => {
    const catalogItemsFlat = (catalog?.categories || []).flatMap((c) => c.items || []).filter((it) => !TOGGLE_ITEM_IDS.has(it.item_id));
    const byId = Object.fromEntries(catalogItemsFlat.map((it) => [it.item_id, it]));
    PREDEFINED_EXTRAS.forEach((it) => { if (!byId[it.id]) byId[it.id] = { item_id: it.id, name: it.name, price: it.price }; });
    const predefinedExtras = Object.entries(extrasQty).filter(([, q]) => (q || 0) > 0).map(([id, q]) => {
      const item = byId[id]; if (!item) return null; return { id, name: item.name, quantity: q, price: Number(item.price) };
    }).filter(Boolean);
    const toggleAddonsForSubmit = Object.entries(selectedAddOns).filter(([, v]) => v).map(([id]) => {
      const a = ADD_ONS.find((x) => x.id === id) || catalogItemsFlat.find((x) => x.item_id === id) || (catalog?.categories || []).flatMap((c) => c.items || []).find((x) => x.item_id === id);
      const price = a?.price; const name = a?.name;
      return price != null ? { id, name, quantity: 1, price: Number(price) } : null;
    }).filter(Boolean);
    const customExtrasForSubmit = customExtras.filter((e) => (parseFloat(e.unit_price) || 0) > 0 && (parseInt(e.quantity, 10) || 0) > 0 && (e.name || '').trim())
      .map((e) => ({ name: e.name.trim().slice(0, 50), unit_price: parseFloat(e.unit_price), quantity: parseInt(e.quantity, 10), catalog_id: e.catalog_id || null }));
    const miscCharges = customChargeAmount && parseFloat(customChargeAmount) > 0 ? [{ name: customChargeDesc || 'Custom charge', unit_price: parseFloat(customChargeAmount), quantity: 1 }] : [];

    const bookingData = {
      package_id: selectedPackage.id,
      date: selectedDate.toISOString().split('T')[0],
      time_slot: selectedTime,
      guest_count: guestCount,
      customer_name: customerName,
      customer_email: customerEmail,
      customer_phone: customerPhone,
      customer_address: customerAddress || null,
      child_name: childName,
      child_age: parseInt(childAge, 10),
      edible_notes: edibleNotes || null,
      add_ons: [...toggleAddonsForSubmit, ...predefinedExtras],
      custom_charges: [...customExtrasForSubmit, ...miscCharges],
      designs: designs.map((d) => ({ garment: d.garment, template_id: d.template_id || null, image_data_url: d.image_data_url || null, label: d.label || '' })),
      discount_code: appliedPromo?.code || null,
      loyalty_voucher_id: appliedFreebieVoucher?.voucher_id || null,
      referral_code: appliedReferral?.code || null,
      gift_card_code: appliedGiftCard?.code || null,
    };

    const res = await axios.post(`${API_URL}/api/bookings`, bookingData, { withCredentials: true });
    const booking = res.data;

    // Submit allergy + consent form against the new booking
    try {
      await axios.post(`${API_URL}/api/allergy-form`, {
        booking_id: booking.booking_id,
        allergies,
        allergy_other: allergyOther || null,
        medical_conditions: medicalConditions,
        medical_other: medicalOther || null,
        emergency_contact_name: emergencyName,
        emergency_contact_phone: emergencyPhone,
        emergency_contact_relationship: emergencyRelationship,
        photo_video_consent: photoConsent,
        liability_waiver_accepted: liabilityAccepted,
        signature_name: signatureName,
        signature_date: new Date().toISOString().split('T')[0],
      });
    } catch (e) { console.error('allergy submit failed', e); }

    return booking;
  };

  // ── Create booking + submit allergy + reveal payment (50% deposit) ─────
  const handleContinueToPayment = async () => {
    if (!validateCheckout()) return;
    setSubmitting(true);
    try {
      const booking = await createBookingWithAllergy();
      setCreatedBooking(booking);
      scrollTo(paymentRef);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to create booking';
      toast.error(detail);
      if (/time slot/i.test(detail) && selectedDate) { await fetchAvailableSlots(selectedDate); setSelectedTime(''); }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Pay Later: create booking then flip to a 24-hour "pending_hold" ────
  const handlePayLater = async () => {
    if (!validateCheckout()) return;
    if (!payLaterAck) { toast.error('Please confirm you understand the 24-hour hold rule.'); return; }
    // Mirror the backend guard: party must be ≥ 24h away (UTC midnight of the party date)
    const partyUtcMidnight = new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()));
    if ((partyUtcMidnight.getTime() - Date.now()) / 3600000 < 24) {
      toast.error('Pay Later requires at least 24 hours before the party. Please pick a later date or pay the deposit now.');
      return;
    }
    setSubmitting(true);
    try {
      const booking = await createBookingWithAllergy();
      await axios.post(`${API_URL}/api/bookings/${booking.booking_id}/defer-payment`, { acknowledged: true });
      try { sessionStorage.removeItem(SS_KEY); } catch { /* noop */ }
      toast.success('Your slot is held for 24 hours — check your email for payment details.');
      setTimeout(() => navigate(`/booking/${booking.booking_id}/held`), 700);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Could not hold your booking';
      toast.error(detail);
      if (/time slot/i.test(detail) && selectedDate) { await fetchAvailableSlots(selectedDate); setSelectedTime(''); }
    } finally {
      setSubmitting(false);
    }
  };

  const onDepositPaid = () => {
    try { sessionStorage.removeItem(SS_KEY); } catch { /* noop */ }
    toast.success('Deposit received — your date is booked! 🎉');
    setTimeout(() => navigate(`/booking/${createdBooking.booking_id}/success`), 900);
  };

  const handlePayPalApprove = async (orderId) => {
    try {
      await axios.post(`${API_URL}/api/payments/paypal/capture`, { booking_id: createdBooking.booking_id, payment_type: 'deposit', order_id: orderId });
      onDepositPaid();
    } catch { toast.error('Failed to capture PayPal payment'); }
  };

  if (loading) {
    return (<div className="min-h-screen bg-[#FFFDF8]"><Header /><div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" /></div></div>);
  }

  const included = selectedPackage?.guests || 0;
  const extraGuestPrice = selectedPackage?.extra_guest_price || 0;

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-b from-[#FFF0F4] to-[#FFFDF8] border-b border-[#F0E9E0]">
        <div className="max-w-6xl mx-auto px-4 py-10 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Build Your Dream Party</h1>
          <p className="text-base md:text-lg text-[#6B6B7A] mt-3">Customise your party and see the total cost instantly.</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8 pb-28 lg:pb-8">
        {/* LEFT — builder */}
        <div className="lg:col-span-2 space-y-8">
          {/* 1. Packages */}
          <section data-testid="pb-packages" className="scroll-mt-24">
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#FF3B6B] text-white flex items-center justify-center font-bold text-sm">1</span>
                <h2 className="text-2xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Choose your package</h2>
              </div>
              {packageCollapsed && selectedPackage && (
                <button type="button" onClick={() => setPackageCollapsed(false)} className="text-sm text-[#FF3B6B] font-semibold flex items-center gap-1 hover:underline" data-testid="pb-edit-package">
                  <Pencil className="w-3.5 h-3.5" /> Change
                </button>
              )}
            </div>
            {packageCollapsed && selectedPackage ? (
              <button type="button" onClick={() => setPackageCollapsed(false)}
                className="w-full flex items-center gap-3 bg-white border-2 border-[#FF3B6B]/30 rounded-2xl p-4 text-left hover:border-[#FF3B6B] transition-colors" data-testid="pb-package-collapsed">
                <CheckCircle2 className="w-6 h-6 text-[#06D6A0] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#1E1E24] truncate">{selectedPackage.name}</p>
                  <p className="text-xs text-[#6B6B7A]">{selectedPackage.duration} · from ${selectedPackage.price.toFixed(2)}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-[#6B6B7A]" />
              </button>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {packages.map((pkg, i) => (
                  <PackageCard key={pkg.id} pkg={pkg} isSelected={selectedPackage?.id === pkg.id} onSelect={() => selectPackage(pkg)} index={i} offer={offerForPackage(pkg)} />
                ))}
              </div>
            )}
          </section>

          {/* 2. Guest slider */}
          {selectedPackage && (
            <section ref={guestsRef} data-testid="pb-guests" className="bg-white rounded-2xl border border-[#E8E5DF] p-6 scroll-mt-24">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-[#FF3B6B] text-white flex items-center justify-center font-bold text-sm">2</span>
                  <h2 className="text-2xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>How many kids?</h2>
                </div>
                {guestsCollapsed && (
                  <button type="button" onClick={() => setGuestsCollapsed(false)} className="text-sm text-[#FF3B6B] font-semibold flex items-center gap-1 hover:underline" data-testid="pb-edit-guests">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                )}
              </div>
              {guestsCollapsed ? (
                <button type="button" onClick={() => setGuestsCollapsed(false)} className="w-full flex items-center gap-3 text-left mt-2 ml-10" data-testid="pb-guests-collapsed">
                  <CheckCircle2 className="w-5 h-5 text-[#06D6A0] flex-shrink-0" />
                  <span className="text-[#1E1E24] font-medium">{guestCount} kids{pricing.extraGuestCount > 0 ? ` · ${pricing.extraGuestCount} extra (+$${pricing.extraGuestTotal.toFixed(2)})` : ''}</span>
                </button>
              ) : (
                <>
                  <p className="text-sm text-[#6B6B7A] mb-5 ml-10">
                    {included} kids included · extra kids ${extraGuestPrice.toFixed(2)} each
                  </p>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <button type="button" onClick={() => setGuestCount((g) => Math.max(1, g - 1))}
                      className="w-11 h-11 rounded-full border-2 border-[#E8E5DF] text-[#FF3B6B] flex items-center justify-center hover:border-[#FF3B6B] disabled:opacity-40 flex-shrink-0"
                      disabled={guestCount <= 1} data-testid="pb-guest-minus"><Minus className="w-5 h-5" /></button>
                    <div className="flex-1 min-w-0">
                      <input type="range" min={1} max={MAX_GUESTS} value={guestCount}
                        onChange={(e) => setGuestCount(parseInt(e.target.value, 10))}
                        className="w-full accent-[#FF3B6B] cursor-pointer h-2" data-testid="pb-guest-slider" />
                      <div className="flex justify-between text-[10px] text-[#9a9aa6] mt-1"><span>1</span><span>{MAX_GUESTS}</span></div>
                    </div>
                    <button type="button" onClick={() => setGuestCount((g) => Math.min(MAX_GUESTS, g + 1))}
                      className="w-11 h-11 rounded-full border-2 border-[#E8E5DF] text-[#FF3B6B] flex items-center justify-center hover:border-[#FF3B6B] disabled:opacity-40 flex-shrink-0"
                      disabled={guestCount >= MAX_GUESTS} data-testid="pb-guest-plus"><Plus className="w-5 h-5" /></button>
                    <div className="text-center min-w-[56px] sm:min-w-[70px]">
                      <p className="text-2xl sm:text-3xl font-bold text-[#1E1E24] tabular-nums" data-testid="pb-guest-count">{guestCount}</p>
                      <p className="text-[10px] uppercase tracking-wide text-[#6B6B7A]">kids</p>
                    </div>
                  </div>
                  {pricing.extraGuestCount > 0 && (
                    <div className="mt-4 flex items-center gap-2 text-sm bg-[#FFF7EB] border border-[#F5C15A] rounded-xl px-4 py-2 text-[#8A5A1F]" data-testid="pb-extra-guest-line">
                      <Users className="w-4 h-4 flex-shrink-0" />
                      {pricing.extraGuestCount} extra {pricing.extraGuestCount === 1 ? 'kid' : 'kids'} × ${extraGuestPrice.toFixed(2)} = <strong>${pricing.extraGuestTotal.toFixed(2)}</strong>
                    </div>
                  )}
                  <Button type="button" variant="outline" onClick={() => { setGuestsCollapsed(true); scrollTo(addonsRef); }}
                    className="mt-5 rounded-full border-[#FF3B6B] text-[#FF3B6B] hover:bg-[#FFEDF3]" data-testid="pb-guests-continue">
                    Continue to extras <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </>
              )}
            </section>
          )}

          {/* 3. Add-ons */}
          {selectedPackage && (
            <section ref={addonsRef} data-testid="pb-addons" className="scroll-mt-24">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-8 h-8 rounded-full bg-[#FF3B6B] text-white flex items-center justify-center font-bold text-sm">3</span>
                <h2 className="text-2xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Add extras <span className="text-sm font-normal text-[#6B6B7A]">(optional)</span></h2>
              </div>

              {catalogLoading ? (
                <div className="py-10 flex items-center justify-center gap-2 text-[#6B6B7A] text-sm"><Loader2 className="w-5 h-5 animate-spin text-[#FF3B6B]" /> Loading add-ons…</div>
              ) : (
                <>
                  {(() => {
                    const cats = (catalog?.categories || []).filter((c) => (c.items || []).length > 0);
                    if (cats.length === 0) return null;
                    const active = cats.find((c) => c.category_id === activeCat) || cats[0];
                    return (
                      <div className="mb-4">
                        {/* Horizontal scrollable category selector */}
                        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x scrollbar-thin" data-testid="addon-cat-tabs">
                          {cats.map((cat) => {
                            const catQty = (cat.items || []).reduce((sum, it) => TOGGLE_ITEM_IDS.has(it.item_id) ? sum + (selectedAddOns[it.item_id] ? 1 : 0) : sum + (extrasQty[it.item_id] || 0), 0);
                            const isActive = active.category_id === cat.category_id;
                            return (
                              <button key={cat.category_id} type="button" onClick={() => setActiveCat(cat.category_id)}
                                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium snap-start transition-colors flex-shrink-0 ${isActive ? 'bg-[#FF3B6B] text-white shadow-sm' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:border-[#FF3B6B]'}`}
                                data-testid={`addon-cat-tab-${cat.category_id}`}>
                                <span>{cat.icon}</span>{cat.name}
                                {catQty > 0 && <span className={`rounded-full text-[10px] px-1.5 py-0.5 leading-none ${isActive ? 'bg-white/25' : 'bg-[#FF3B6B] text-white'}`}>{catQty}</span>}
                              </button>
                            );
                          })}
                        </div>
                        {/* Active category — compact responsive grid */}
                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5" data-testid={`addon-grid-${active.category_id}`}>
                          {active.items.map((it) => (
                            <AddonItemCard key={it.item_id} item={it} isToggle={TOGGLE_ITEM_IDS.has(it.item_id)}
                              toggleValue={!!selectedAddOns[it.item_id]} qtyValue={extrasQty[it.item_id] || 0}
                              onToggle={(v) => setSelectedAddOns((s) => ({ ...s, [it.item_id]: v }))}
                              onQtyChange={(q) => setExtrasQty((s) => ({ ...s, [it.item_id]: q }))} />
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Design Studio */}
                  {(() => {
                    const printAddons = Object.keys(PRINT_GARMENT_MAP).filter((id) => (extrasQty[id] || 0) > 0);
                    const canDesign = printAddons.length > 0;
                    return (
                      <div className="mb-4 rounded-2xl p-5 border-2 border-dashed" style={{ borderColor: canDesign ? '#FF3B6B' : '#E8E5DF', backgroundColor: canDesign ? '#FFF0F3' : '#FFFDF8' }} data-testid="design-studio-section">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="flex items-start gap-2">
                            <Palette className="w-5 h-5 text-[#FF3B6B] mt-0.5" />
                            <div>
                              <h4 className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Design Studio</h4>
                              <p className="text-xs text-[#6B6B7A]">{canDesign ? 'Design your printed items — upload a photo or pick a template.' : 'Add Print-a-Cap, T-Shirt or Mug above to unlock.'}</p>
                            </div>
                          </div>
                          <Button type="button" onClick={() => openDesignStudio(PRINT_GARMENT_MAP[printAddons[0]] || 'tshirt')} disabled={!canDesign}
                            className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white disabled:opacity-50" data-testid="open-design-studio-btn">
                            <Sparkles className="w-4 h-4 mr-1.5" />{designs.length > 0 ? 'Add another' : 'Open Design Studio'}
                          </Button>
                        </div>
                        {designs.length > 0 && (
                          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="saved-designs-list">
                            {designs.map((d) => (
                              <div key={d.id} className="relative bg-white rounded-xl border border-[#E8E5DF] p-2 flex items-center gap-2">
                                <span className="text-2xl">{d.garment === 'cap' ? '🧢' : d.garment === 'mug' ? '☕' : '👕'}</span>
                                <div className="min-w-0"><p className="text-xs font-semibold capitalize truncate">{d.garment === 'tshirt' ? 'T-Shirt' : d.garment}</p></div>
                                <button type="button" onClick={() => removeDesign(d.id)} className="absolute top-1 right-1 w-6 h-6 rounded-full text-[#FF3B6B] hover:bg-[#FF3B6B]/10 flex items-center justify-center"><Trash2 className="w-3 h-3" /></button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Custom Extras */}
                  <div className="mb-4 rounded-2xl bg-white border border-[#E8E5DF] p-5" data-testid="pb-custom-extras">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>➕ Custom Extras</h3>
                      {customExtras.length > 0 && <Badge className="bg-[#FF3B6B] hover:bg-[#FF3B6B] text-white rounded-full text-[10px]">{customExtras.length} added</Badge>}
                    </div>
                    {customExtras.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                        {customExtras.map((ce) => (
                          <div key={ce.id} className="flex items-center gap-3 bg-[#FFEDF3] border border-[#FF3B6B] rounded-xl p-3" data-testid={`custom-extra-${ce.id}`}>
                            <div className="flex-1 min-w-0"><p className="font-medium text-sm truncate">{ce.name}</p><p className="text-[11px] text-[#6B6B7A]">{ce.quantity} × ${Number(ce.unit_price || 0).toFixed(2)} = <strong className="text-[#FF3B6B]">${(Number(ce.unit_price || 0) * Number(ce.quantity || 0)).toFixed(2)}</strong></p></div>
                            <Button type="button" size="sm" variant="ghost" onClick={() => setCustomExtras((a) => a.filter((x) => x.id !== ce.id))} className="h-7 w-7 p-0 text-[#D4302A]"><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {!showCustomExtraForm ? (
                      <Button type="button" size="sm" variant="outline" onClick={() => { setShowCustomExtraForm(true); setCustomExtraDraft({ name: '', unit_price: '', quantity: 1 }); }}
                        className="rounded-full border-dashed border-[#FF3B6B] text-[#FF3B6B] hover:bg-[#FFEDF3]" data-testid="add-custom-extra-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Custom Extra</Button>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_90px_auto] gap-2 items-end p-3 bg-[#FFFDF8] border border-[#E8E5DF] rounded-xl">
                        <div><label className="block text-[10px] uppercase text-[#6B6B7A] mb-1">Item</label><Input value={customExtraDraft.name} onChange={(e) => setCustomExtraDraft((d) => ({ ...d, name: e.target.value }))} placeholder="Extra hour, host tip…" className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="custom-extra-name-input" /></div>
                        <div><label className="block text-[10px] uppercase text-[#6B6B7A] mb-1">Price ($)</label><Input type="number" step="0.01" min="0" value={customExtraDraft.unit_price} onChange={(e) => setCustomExtraDraft((d) => ({ ...d, unit_price: e.target.value }))} className="h-9 rounded-xl border-[#E8E5DF] text-sm text-right" data-testid="custom-extra-price-input" /></div>
                        <div><label className="block text-[10px] uppercase text-[#6B6B7A] mb-1">Qty</label><Input type="number" min="1" max="99" value={customExtraDraft.quantity} onChange={(e) => setCustomExtraDraft((d) => ({ ...d, quantity: Math.max(1, parseInt(e.target.value, 10) || 1) }))} className="h-9 rounded-xl border-[#E8E5DF] text-sm text-right" data-testid="custom-extra-qty-input" /></div>
                        <div className="flex gap-1">
                          <Button type="button" size="sm" onClick={() => {
                            const name = (customExtraDraft.name || '').trim(); const price = parseFloat(customExtraDraft.unit_price); const q = parseInt(customExtraDraft.quantity, 10) || 1;
                            if (!name) { toast.error('Item name is required'); return; } if (!Number.isFinite(price) || price <= 0) { toast.error('Price must be > 0'); return; }
                            setCustomExtras((a) => [...a, { id: `custom_${Date.now().toString(36)}`, name, unit_price: price, quantity: q }]); setShowCustomExtraForm(false);
                          }} className="h-9 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white text-xs" data-testid="custom-extra-add-confirm">Add</Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => setShowCustomExtraForm(false)} className="h-9 rounded-full text-xs">Cancel</Button>
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-2 mt-4 pt-4 border-t border-[#F0EDE6]">
                      <Input value={customChargeDesc} onChange={(e) => setCustomChargeDesc(e.target.value)} placeholder="Misc charge description (optional)" className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="custom-charge-desc" />
                      <Input type="number" step="0.01" min="0" value={customChargeAmount} onChange={(e) => setCustomChargeAmount(e.target.value)} placeholder="$ Amount" className="h-9 rounded-xl border-[#E8E5DF] text-sm text-right" data-testid="custom-charge-amount" />
                    </div>
                  </div>
                </>
              )}
            </section>
          )}

          {/* ── CHECKOUT (revealed after Hold) ── */}
          {showCheckout && selectedPackage && (
            <div ref={checkoutRef} className="space-y-8 scroll-mt-24">
              {/* Date & Time */}
              <section className="bg-white rounded-2xl border border-[#E8E5DF] p-6" data-testid="pb-datetime">
                <div className="flex items-center gap-2 mb-5"><CalendarDays className="w-5 h-5 text-[#FF3B6B]" /><h2 className="text-xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Pick your date & time</h2></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-[#1E1E24] font-semibold mb-2 block">Date</Label>
                    <Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate} disabled={isDateDisabled} className="rounded-xl border border-[#E8E5DF] p-3" data-testid="booking-calendar" />
                  </div>
                  <div>
                    <Label className="text-[#1E1E24] font-semibold mb-2 block">Time</Label>
                    {selectedDate ? (
                      <div className="space-y-3">
                        {timeSlots.map((slot) => {
                          const isAvail = availableSlots.includes(slot);
                          return (
                            <button key={slot} onClick={() => isAvail && setSelectedTime(slot)} disabled={!isAvail}
                              className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${selectedTime === slot ? 'border-[#FF3B6B] bg-[#FF3B6B]/5 text-[#FF3B6B]' : isAvail ? 'border-[#E8E5DF] hover:border-[#FF3B6B]/50' : 'border-[#E8E5DF] bg-gray-50 text-[#6B6B7A] cursor-not-allowed'}`}
                              data-testid={`time-slot-${slot}`}>
                              <Clock className="w-5 h-5" /><span className="font-medium">{SLOT_LABEL[slot] || slot}</span>{!isAvail && <span className="ml-auto text-sm">(Booked)</span>}
                            </button>
                          );
                        })}
                      </div>
                    ) : (<p className="text-[#6B6B7A] p-4 border border-dashed border-[#E8E5DF] rounded-xl text-center">Select a date first</p>)}
                  </div>
                </div>
              </section>

              {/* Child & Parent details */}
              <section className="bg-white rounded-2xl border border-[#E8E5DF] p-6" data-testid="pb-details">
                <div className="flex items-center gap-2 mb-5"><User className="w-5 h-5 text-[#FF3B6B]" /><h2 className="text-xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Child & parent details</h2></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label className="font-semibold">Birthday child's name *</Label><Input value={childName} onChange={(e) => setChildName(e.target.value)} className="mt-1.5 rounded-xl border-[#E8E5DF]" data-testid="input-child-name" /></div>
                  <div><Label className="font-semibold">Child's age *</Label><Input type="number" min="1" max="17" value={childAge} onChange={(e) => setChildAge(e.target.value)} placeholder="e.g. 7" className="mt-1.5 rounded-xl border-[#E8E5DF]" data-testid="input-child-age" /></div>
                  <div><Label className="font-semibold">Your full name *</Label><Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="mt-1.5 rounded-xl border-[#E8E5DF]" data-testid="input-customer-name" /></div>
                  <div><Label className="font-semibold">Email *</Label><Input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} className="mt-1.5 rounded-xl border-[#E8E5DF]" data-testid="input-customer-email" /></div>
                  <div><Label className="font-semibold">Phone *</Label><Input type="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} className="mt-1.5 rounded-xl border-[#E8E5DF]" data-testid="input-customer-phone" /></div>
                  <div><Label className="font-semibold">Address <span className="text-[#6B6B7A] font-normal">(optional)</span></Label><Input value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} className="mt-1.5 rounded-xl border-[#E8E5DF]" data-testid="input-customer-address" /></div>
                </div>
                <div className="mt-4"><Label className="font-semibold">Special requests / edible bouquet notes</Label><Textarea rows={2} value={edibleNotes} onChange={(e) => setEdibleNotes(e.target.value)} className="mt-1.5 rounded-xl border-[#E8E5DF]" data-testid="edible-notes" /></div>
              </section>

              {/* Allergy & consent */}
              <section className="bg-white rounded-2xl border border-[#E8E5DF] p-6" data-testid="pb-allergy">
                <div className="flex items-center gap-2 mb-5"><HeartPulse className="w-5 h-5 text-[#FF3B6B]" /><h2 className="text-xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Allergy & consent form</h2></div>
                <div className="mb-4">
                  <Label className="font-semibold mb-2 block">Allergies (select all that apply)</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {allergyOptions.map((o) => (
                      <label key={o.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm ${allergies.includes(o.id) ? 'border-[#FF3B6B] bg-[#FFEDF3]' : 'border-[#E8E5DF]'}`} data-testid={`allergy-${o.id}`}>
                        <Checkbox checked={allergies.includes(o.id)} onCheckedChange={() => toggleAllergy(o.id)} />{o.label}
                      </label>
                    ))}
                  </div>
                  {allergies.includes('other') && <Input value={allergyOther} onChange={(e) => setAllergyOther(e.target.value)} placeholder="Describe other allergies" className="mt-2 rounded-xl border-[#E8E5DF]" data-testid="allergy-other" />}
                </div>
                <div className="mb-4">
                  <Label className="font-semibold mb-2 block">Medical conditions</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {medicalOptions.map((o) => (
                      <label key={o.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm ${medicalConditions.includes(o.id) ? 'border-[#FF3B6B] bg-[#FFEDF3]' : 'border-[#E8E5DF]'}`} data-testid={`medical-${o.id}`}>
                        <Checkbox checked={medicalConditions.includes(o.id)} onCheckedChange={() => toggleMedical(o.id)} />{o.label}
                      </label>
                    ))}
                  </div>
                  {medicalConditions.includes('other') && <Input value={medicalOther} onChange={(e) => setMedicalOther(e.target.value)} placeholder="Describe other conditions" className="mt-2 rounded-xl border-[#E8E5DF]" data-testid="medical-other" />}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  <div><Label className="font-semibold text-xs">Emergency contact *</Label><Input value={emergencyName} onChange={(e) => setEmergencyName(e.target.value)} placeholder="Name" className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="emergency-name" /></div>
                  <div><Label className="font-semibold text-xs">Phone *</Label><Input value={emergencyPhone} onChange={(e) => setEmergencyPhone(e.target.value)} placeholder="Phone" className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="emergency-phone" /></div>
                  <div><Label className="font-semibold text-xs">Relationship *</Label><Input value={emergencyRelationship} onChange={(e) => setEmergencyRelationship(e.target.value)} placeholder="e.g. Parent" className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="emergency-relationship" /></div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-[#FFFDF8] border border-[#E8E5DF] mb-3">
                  <span className="text-sm text-[#1E1E24]">I consent to photos/videos being taken</span>
                  <Switch checked={photoConsent} onCheckedChange={setPhotoConsent} data-testid="photo-consent" />
                </div>
                <div className="rounded-xl border border-[#E8E5DF] p-3 mb-3" data-testid="liability-waiver-block">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      checked={liabilityAccepted}
                      onCheckedChange={(v) => {
                        if (!waiverRead) { toast.info('Please read the full waiver first.'); return; }
                        setLiabilityAccepted(!!v);
                      }}
                      className={`mt-0.5 ${waiverRead ? '' : 'opacity-40'}`}
                      data-testid="liability-waiver"
                    />
                    <div className="flex-1">
                      <span className={`text-sm ${waiverRead ? 'text-[#6B6B7A]' : 'text-[#B0AEA8]'}`}>
                        I accept the liability waiver and confirm the information provided is accurate. *
                      </span>
                      {!waiverRead && (
                        <span className="block text-[11px] text-[#B0603A] mt-0.5" data-testid="liability-locked-hint">
                          Read the full waiver to enable this checkbox.
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setWaiverOpen(true)}
                    className="mt-2 ml-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF3B6B] hover:underline"
                    data-testid="read-waiver-btn"
                  >
                    <FileText className="w-3.5 h-3.5" /> Read Full Liability Waiver
                    {waiverRead && <Check className="w-3.5 h-3.5 text-[#06D6A0]" />}
                  </button>
                </div>
                <div><Label className="font-semibold text-xs">Signature (type your full name) *</Label><Input value={signatureName} onChange={(e) => setSignatureName(e.target.value)} placeholder="Your full name" className="mt-1 rounded-xl border-[#E8E5DF] font-medium" data-testid="signature-name" /></div>
              </section>

              {/* Continue to payment / Payment */}
              {!createdBooking ? (
                <div className="space-y-4" data-testid="pb-final-action">
                  {/* Choose how to secure the date */}
                  <div className="grid grid-cols-2 gap-2" data-testid="pb-hold-mode-toggle">
                    <button type="button" onClick={() => setHoldMode('deposit')}
                      className={`p-3 rounded-xl border-2 text-sm font-semibold transition-colors ${holdMode === 'deposit' ? 'border-[#FF3B6B] bg-[#FFEDF3] text-[#FF3B6B]' : 'border-[#E8E5DF] text-[#6B6B7A] hover:border-[#FF3B6B]/50'}`}
                      data-testid="pb-mode-deposit">
                      <CreditCard className="w-4 h-4 inline mr-1.5" /> Pay 50% deposit now
                    </button>
                    <button type="button" onClick={() => setHoldMode('pay_later')}
                      className={`p-3 rounded-xl border-2 text-sm font-semibold transition-colors ${holdMode === 'pay_later' ? 'border-[#FF7A2E] bg-[#FFF4E6] text-[#B8860B]' : 'border-[#E8E5DF] text-[#6B6B7A] hover:border-[#FF7A2E]/50'}`}
                      data-testid="pb-mode-pay-later">
                      <CalendarClock className="w-4 h-4 inline mr-1.5" /> Pay later · 24h hold
                    </button>
                  </div>

                  {holdMode === 'deposit' ? (
                    <Button onClick={handleContinueToPayment} disabled={submitting}
                      className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full py-6 text-base font-semibold" data-testid="pb-continue-to-payment">
                      {submitting ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Creating your booking…</>) : (<>Continue to Payment · Pay ${pricing.deposit.toFixed(2)} deposit <ArrowRight className="w-5 h-5 ml-2" /></>)}
                    </Button>
                  ) : (
                    <div className="bg-[#FFF8E6] border-2 border-[#FFD166] rounded-2xl p-4 space-y-3" data-testid="pb-pay-later-panel">
                      <div className="flex items-start gap-2">
                        <CalendarClock className="w-5 h-5 text-[#B8860B] mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-[#1E1E24]">Hold my slot for 24 hours</p>
                          <p className="text-sm text-[#6B6B7A] mt-0.5">
                            We'll reserve your party slot for the next 24 hours while you arrange payment. Pay at least the 50% deposit (${pricing.deposit.toFixed(2)}) — or the full amount — before the hold expires to confirm your booking.
                          </p>
                        </div>
                      </div>
                      <label className="flex items-start gap-3 p-3 rounded-xl bg-white border-2 border-[#FFD166] cursor-pointer hover:border-[#FF7A2E] transition" data-testid="pb-pay-later-ack">
                        <input type="checkbox" checked={payLaterAck} onChange={(e) => setPayLaterAck(e.target.checked)}
                          className="mt-0.5 w-4 h-4 accent-[#FF3B6B] flex-shrink-0" data-testid="pb-pay-later-ack-checkbox" />
                        <span className="text-sm text-[#1E1E24] leading-snug">
                          I understand my booking is held for <strong>24 hours</strong>. I must pay the <strong>50% deposit</strong> or the full amount to confirm. If I don't pay in time, the slot is released and my booking is automatically cancelled.
                        </span>
                      </label>
                      <Button onClick={handlePayLater} disabled={submitting || !payLaterAck}
                        className="w-full bg-[#FF7A2E] hover:bg-[#E66A1E] text-white rounded-full py-6 text-base font-semibold disabled:opacity-40" data-testid="pb-hold-24h-btn">
                        {submitting ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Holding your slot…</>) : (<>Hold Booking for 24 Hours <ArrowRight className="w-5 h-5 ml-2" /></>)}
                      </Button>
                      <p className="text-xs text-[#6B6B7A] text-center">No payment now — we'll email you a payment link to confirm.</p>
                    </div>
                  )}
                </div>
              ) : (
                <section ref={paymentRef} className="bg-white rounded-2xl border-2 border-[#FF3B6B]/30 p-6 scroll-mt-24" data-testid="pb-payment">
                  <div className="flex items-center gap-2 mb-1"><CreditCard className="w-5 h-5 text-[#FF3B6B]" /><h2 className="text-xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Pay your 50% deposit</h2></div>
                  <p className="text-sm text-[#6B6B7A] mb-5">Booking <strong>{createdBooking.booking_reference}</strong> is reserved. Pay <strong>${Number(createdBooking.deposit_amount || pricing.deposit).toFixed(2)} CAD</strong> to confirm your date.</p>
                  <div className="flex gap-2 mb-5">
                    <button type="button" onClick={() => setPaymentMethod('card')} className={`flex-1 p-3 rounded-xl border-2 flex items-center justify-center gap-2 text-sm font-semibold ${paymentMethod === 'card' ? 'border-[#FF3B6B] bg-[#FFEDF3] text-[#FF3B6B]' : 'border-[#E8E5DF] text-[#6B6B7A]'}`} data-testid="pay-method-card"><CreditCard className="w-4 h-4" /> Credit / Debit Card</button>
                    <button type="button" onClick={() => setPaymentMethod('paypal')} className={`flex-1 p-3 rounded-xl border-2 flex items-center justify-center gap-2 text-sm font-semibold ${paymentMethod === 'paypal' ? 'border-[#FF3B6B] bg-[#FFEDF3] text-[#FF3B6B]' : 'border-[#E8E5DF] text-[#6B6B7A]'}`} data-testid="pay-method-paypal">PayPal</button>
                  </div>
                  {paymentMethod === 'card' ? (
                    <StripeCardForm context="customer" bookingId={createdBooking.booking_id} amount={Number(createdBooking.deposit_amount || pricing.deposit)} paymentType="deposit" onSuccess={onDepositPaid} />
                  ) : paypalConfig?.configured && paypalConfig?.client_id ? (
                    <PayPalScriptProvider options={{ clientId: paypalConfig.client_id, currency: 'CAD', intent: 'capture' }}>
                      <PayPalButtons style={{ layout: 'vertical', shape: 'pill', color: 'gold' }}
                        createOrder={async () => { const r = await axios.post(`${API_URL}/api/payments/paypal/create-order`, { booking_id: createdBooking.booking_id, payment_type: 'deposit', origin_url: window.location.origin }); return r.data.order_id || r.data.id; }}
                        onApprove={(data) => handlePayPalApprove(data.orderID)}
                        onError={() => toast.error('PayPal payment failed')} />
                    </PayPalScriptProvider>
                  ) : (
                    <div className="p-4 rounded-xl bg-[#FFF4E6] border border-[#FFD166] text-sm text-[#8B6914]" data-testid="paypal-not-configured">PayPal isn't available right now — please pay by card.</div>
                  )}
                </section>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — sticky summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-6 lg:sticky lg:top-24" data-testid="pb-summary">
            <h3 className="text-lg font-semibold text-[#1E1E24] mb-4" style={{ fontFamily: 'Fredoka' }}>Your Party Summary</h3>
            {selectedPackage ? (
              <div className="space-y-3">
                <div className="p-3 bg-[#FFFDF8] rounded-xl">
                  <p className="font-semibold text-[#1E1E24]">{selectedPackage.name}</p>
                  <p className="text-xs text-[#6B6B7A]">{selectedPackage.duration} · {guestCount} kids</p>
                </div>
                {selectedDate && selectedTime && (
                  <div className="flex justify-between text-sm"><span className="text-[#6B6B7A]">When</span><span className="text-[#1E1E24] font-medium">{selectedDate.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}, {SLOT_LABEL[selectedTime]}</span></div>
                )}

                <div className="border-t border-[#E8E5DF] pt-3 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-[#6B6B7A]">Base package</span><span className="text-[#1E1E24] tabular-nums">${selectedPackage.price.toFixed(2)}</span></div>
                  {pricing.extraGuestCount > 0 && (
                    <div className="flex justify-between text-sm" data-testid="summary-extra-guests"><span className="text-[#6B6B7A]">Extra kids ({pricing.extraGuestCount} × ${extraGuestPrice.toFixed(2)})</span><span className="text-[#1E1E24] tabular-nums">${pricing.extraGuestTotal.toFixed(2)}</span></div>
                  )}
                  {(catalog?.categories || []).map((cat) => {
                    const lines = [];
                    (cat.items || []).forEach((it) => {
                      if (TOGGLE_ITEM_IDS.has(it.item_id)) { if (selectedAddOns[it.item_id]) lines.push({ key: it.item_id, label: it.name, qty: 1, amount: Number(it.price) || 0 }); }
                      else { const q = extrasQty[it.item_id] || 0; if (q > 0) lines.push({ key: it.item_id, label: it.name, qty: q, amount: (Number(it.price) || 0) * q }); }
                    });
                    if (lines.length === 0) return null;
                    const catTotal = lines.reduce((s, l) => s + l.amount, 0);
                    return (
                      <div key={cat.category_id} className="pt-1" data-testid={`summary-cat-${cat.category_id}`}>
                        <div className="flex justify-between text-sm"><span className="text-[#B8860B] font-medium">{cat.icon} {cat.name}</span><span className="text-[#B8860B] font-medium tabular-nums">${catTotal.toFixed(2)}</span></div>
                        {lines.map((l) => (<div key={l.key} className="flex justify-between text-xs pl-4"><span className="text-[#6B6B7A] truncate mr-2">{l.label}{l.qty > 1 ? ` × ${l.qty}` : ''}</span><span className="text-[#6B6B7A] tabular-nums">${l.amount.toFixed(2)}</span></div>))}
                      </div>
                    );
                  })}
                  {customExtras.length > 0 && (
                    <div className="pt-1" data-testid="summary-cat-custom">
                      <div className="flex justify-between text-sm"><span className="text-[#6366F1] font-medium">➕ Custom Extras</span><span className="text-[#6366F1] font-medium tabular-nums">${pricing.customExtrasTotal.toFixed(2)}</span></div>
                    </div>
                  )}
                  {parseFloat(customChargeAmount) > 0 && (<div className="flex justify-between text-sm"><span className="text-[#06D6A0]">{customChargeDesc || 'Misc'}</span><span className="text-[#06D6A0] tabular-nums">${parseFloat(customChargeAmount).toFixed(2)}</span></div>)}
                </div>

                {/* Promo */}
                <div className="pt-3 border-t border-dashed border-[#E8E5DF]">
                  {appliedPromo ? (
                    <div className="flex items-center justify-between gap-2 p-2 rounded-xl bg-[#06D6A0]/10" data-testid="promo-applied">
                      <p className="text-xs font-semibold text-[#058B69] truncate">{appliedPromo.code} applied</p>
                      <button type="button" onClick={() => { setAppliedPromo(null); setPromoInput(''); }} className="text-xs text-[#058B69] underline" data-testid="promo-remove">remove</button>
                    </div>
                  ) : (
                    <div>
                      <label className="text-xs text-[#6B6B7A] block mb-1.5">Promo code</label>
                      <div className="flex gap-2">
                        <Input value={promoInput} onChange={(e) => { setPromoInput(e.target.value.toUpperCase().slice(0, 32)); setPromoError(''); }} placeholder="Enter code" className="h-9 rounded-xl border-[#E8E5DF] font-mono text-sm flex-1" data-testid="promo-input" />
                        <Button type="button" onClick={() => validatePromo()} disabled={promoValidating || !promoInput.trim()} className="h-9 px-3 rounded-xl bg-[#1E1E24] text-white text-xs" data-testid="promo-apply">{promoValidating ? '…' : 'Apply'}</Button>
                      </div>
                      {promoError && <p className="text-xs text-[#FF3B6B] mt-1" data-testid="promo-error">{promoError}</p>}
                    </div>
                  )}
                </div>

                {/* Gift card */}
                <div className="pt-3 border-t border-dashed border-[#E8E5DF]" data-testid="gift-card-section">
                  {appliedGiftCard ? (
                    <div className="flex items-center justify-between gap-2 p-2 rounded-xl bg-[#FB923C]/10" data-testid="gift-card-applied">
                      <p className="text-xs font-semibold text-[#C2410C] truncate"><Gift className="w-3 h-3 inline mr-1" />{appliedGiftCard.code}</p>
                      <button type="button" onClick={() => { setAppliedGiftCard(null); setGiftCardInput(''); }} className="text-xs text-[#C2410C] underline" data-testid="gift-card-remove">remove</button>
                    </div>
                  ) : (
                    <div>
                      <label className="text-xs text-[#6B6B7A] block mb-1.5">Gift card <span className="opacity-60">(optional)</span></label>
                      <div className="flex gap-2">
                        <Input value={giftCardInput} onChange={(e) => { setGiftCardInput(e.target.value.toUpperCase().slice(0, 32)); setGiftCardError(''); }} placeholder="GIFT-XXXX" className="h-9 rounded-xl border-[#E8E5DF] font-mono text-sm flex-1" data-testid="gift-card-input" />
                        <Button type="button" onClick={() => validateGiftCard()} disabled={giftCardValidating || !giftCardInput.trim()} className="h-9 px-3 rounded-xl bg-[#FB923C] text-white text-xs" data-testid="gift-card-apply">{giftCardValidating ? '…' : 'Apply'}</Button>
                      </div>
                      {giftCardError && <p className="text-xs text-[#FF3B6B] mt-1" data-testid="gift-card-error">{giftCardError}</p>}
                    </div>
                  )}
                </div>

                {/* Referral */}
                <div className="pt-3 border-t border-dashed border-[#E8E5DF]" data-testid="referral-section">
                  {appliedReferral ? (
                    <div className="flex items-center justify-between gap-2 p-2 rounded-xl bg-[#6366F1]/10" data-testid="referral-applied">
                      <p className="text-xs font-semibold text-[#4F46E5] truncate">Referred by {appliedReferral.referrer_first_name}</p>
                      <button type="button" onClick={() => { setAppliedReferral(null); setReferralInput(''); }} className="text-xs text-[#4F46E5] underline" data-testid="referral-remove">remove</button>
                    </div>
                  ) : (
                    <div>
                      <label className="text-xs text-[#6B6B7A] block mb-1.5">Referral code <span className="opacity-60">(optional)</span></label>
                      <div className="flex gap-2">
                        <Input value={referralInput} onChange={(e) => { setReferralInput(e.target.value.toUpperCase().slice(0, 20)); setReferralError(''); }} placeholder="REF-XXXXXX" className="h-9 rounded-xl border-[#E8E5DF] font-mono text-sm flex-1" data-testid="referral-input" />
                        <Button type="button" onClick={() => validateReferral()} disabled={referralValidating || !referralInput.trim()} className="h-9 px-3 rounded-xl bg-[#6366F1] text-white text-xs" data-testid="referral-apply">{referralValidating ? '…' : 'Apply'}</Button>
                      </div>
                      {referralError && <p className="text-xs text-[#FF3B6B] mt-1" data-testid="referral-error">{referralError}</p>}
                    </div>
                  )}
                </div>

                {/* Loyalty vouchers */}
                {loyaltyVouchers.length > 0 && (
                  <div className="pt-3 border-t border-dashed border-[#E8E5DF]" data-testid="loyalty-vouchers">
                    <label className="text-xs text-[#6B6B7A] block mb-1.5 flex items-center gap-1"><Gift className="w-3 h-3 text-[#FF3B6B]" /> Loyalty rewards</label>
                    <div className="space-y-1.5">
                      {loyaltyVouchers.map((v) => {
                        const isFreebie = v.type === 'freebie';
                        const applied = (isFreebie && appliedFreebieVoucher?.voucher_id === v.voucher_id) || (!isFreebie && appliedPromo?.code === v.discount_code);
                        return (
                          <div key={v.voucher_id} className={`flex items-center justify-between gap-2 p-2 rounded-xl ${applied ? 'bg-[#06D6A0]/10' : 'bg-[#FFFDF8] border border-[#E8E5DF]'}`}>
                            <p className="text-xs font-semibold text-[#1E1E24] truncate">{v.reward_name}</p>
                            {applied ? (<button type="button" onClick={() => isFreebie ? setAppliedFreebieVoucher(null) : setAppliedPromo(null)} className="text-xs text-[#058B69] underline">remove</button>)
                              : (<Button type="button" onClick={() => isFreebie ? applyFreebieVoucher(v) : applyDiscountVoucher(v)} className="h-7 px-2 rounded-full bg-[#FF3B6B] text-white text-[11px]">Apply</Button>)}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Totals */}
                <div className="pt-3 border-t border-[#E8E5DF] space-y-1.5">
                  <div className="flex justify-between text-sm"><span className="text-[#6B6B7A]">Subtotal</span><span className="text-[#1E1E24] tabular-nums">${pricing.preDiscountSubtotal.toFixed(2)}</span></div>
                  {pricing.specialOfferAmount > 0 && <div className="flex justify-between text-sm" data-testid="summary-special-offer"><span className="text-[#FF3B6B]">★ {pricing.specialOffer?.title || 'Offer'}</span><span className="text-[#FF3B6B]">− ${pricing.specialOfferAmount.toFixed(2)}</span></div>}
                  {pricing.discountAmount > 0 && <div className="flex justify-between text-sm" data-testid="summary-discount"><span className="text-[#06D6A0]">Discount</span><span className="text-[#06D6A0]">− ${pricing.discountAmount.toFixed(2)}</span></div>}
                  {pricing.freebieAmount > 0 && <div className="flex justify-between text-sm"><span className="text-[#06D6A0]">Loyalty freebie</span><span className="text-[#06D6A0]">− ${pricing.freebieAmount.toFixed(2)}</span></div>}
                  {pricing.giftCardAmount > 0 && <div className="flex justify-between text-sm" data-testid="summary-gift-card"><span className="text-[#FB923C]">Gift card</span><span className="text-[#FB923C]">− ${pricing.giftCardAmount.toFixed(2)}</span></div>}
                  <div className="flex justify-between text-sm"><span className="text-[#6B6B7A]">HST (13%)</span><span className="text-[#1E1E24] tabular-nums">${pricing.tax.toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold text-lg pt-2 border-t border-[#E8E5DF]"><span className="text-[#1E1E24]">Total</span><span className="text-[#FF3B6B] tabular-nums" data-testid="summary-total">${pricing.total.toFixed(2)}</span></div>
                  <div className="flex justify-between text-sm bg-[#06D6A0]/10 p-3 rounded-xl mt-2"><span className="text-[#06D6A0] font-medium">Deposit (50%)</span><span className="text-[#06D6A0] font-semibold tabular-nums" data-testid="summary-deposit">${pricing.deposit.toFixed(2)}</span></div>
                  {loyaltyRate && pricing.subtotal > 0 && (
                    <div className="flex items-start gap-2 bg-[#FFF4E6] border border-[#FFD9A8] p-3 rounded-xl mt-2" data-testid="summary-loyalty-points">
                      <Star className="w-4 h-4 text-[#E8A80E] flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-[#8A6A12]">
                        You'll earn approximately{' '}
                        <span className="font-bold text-[#B8860B]">{Math.floor(pricing.subtotal * (loyaltyRate.points_per_dollar || 2)).toLocaleString()} points</span>{' '}
                        from this booking.
                        {loyaltyRate.source === 'promo' && loyaltyRate.promo && (
                          <span className="block text-[#FF3B6B] font-semibold mt-0.5">🎉 {loyaltyRate.promo.name} — earn {loyaltyRate.points_per_dollar} pts per $1!</span>
                        )}
                      </p>
                    </div>
                  )}
                </div>

                {!showCheckout && (
                  <div className="mt-4 space-y-2">
                    <Button onClick={() => handleHold('deposit')} disabled={!selectedPackage} className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full py-6 text-base font-semibold" data-testid="pb-hold-btn">
                      Hold Date with 50% Deposit
                    </Button>
                    <Button onClick={() => handleHold('pay_later')} disabled={!selectedPackage} variant="outline" className="w-full border-2 border-[#FF7A2E] text-[#B8860B] hover:bg-[#FFF4E6] rounded-full py-6 text-base font-semibold" data-testid="pb-pay-later-btn">
                      <CalendarClock className="w-4 h-4 mr-2" /> Hold Booking for 24 Hours (Pay Later)
                    </Button>
                  </div>
                )}
                <div className="flex items-center justify-center gap-1.5 text-[11px] text-[#6B6B7A] mt-3">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#06D6A0]" /> Free cancellation up to 24 hours before your party
                </div>
              </div>
            ) : (
              <p className="text-[#6B6B7A] text-center py-8">Select a package to see pricing</p>
            )}
          </div>
        </div>
      </div>

      {/* Mobile sticky bottom bar */}
      {selectedPackage && !showCheckout && (
        <div className="fixed bottom-0 left-0 right-0 z-30 lg:hidden bg-white/95 backdrop-blur border-t border-[#E8E5DF] shadow-[0_-4px_20px_rgba(0,0,0,0.08)] px-4 py-3 flex flex-col gap-2" data-testid="pb-mobile-bar">
          <div className="flex items-center gap-3">
            <div className="flex-1"><p className="text-[10px] uppercase text-[#6B6B7A]">Total</p><p className="text-xl font-bold text-[#FF3B6B] tabular-nums">${pricing.total.toFixed(2)}</p></div>
            <Button onClick={() => handleHold('deposit')} className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full px-5 py-5 font-semibold" data-testid="pb-hold-btn-mobile">Hold · 50% Deposit</Button>
          </div>
          <Button onClick={() => handleHold('pay_later')} variant="outline" className="w-full border-2 border-[#FF7A2E] text-[#B8860B] hover:bg-[#FFF4E6] rounded-full py-3 text-sm font-semibold" data-testid="pb-pay-later-btn-mobile">
            <CalendarClock className="w-4 h-4 mr-2" /> Hold 24 Hours (Pay Later)
          </Button>
        </div>
      )}

      <DesignStudioModal open={designOpen} onOpenChange={setDesignOpen} initialGarment={designInitialGarment} childName={childName} onSave={onDesignSaved} />

      {/* Liability waiver modal */}
      <Dialog open={waiverOpen} onOpenChange={setWaiverOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[88vh] flex flex-col p-0" data-testid="waiver-modal">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#FF3B6B]" /> Liability Waiver & Party Agreement</DialogTitle>
          </DialogHeader>
          <div className="px-6 py-3 overflow-y-auto space-y-4 flex-1" data-testid="waiver-text">
            <p className="text-sm text-[#6B6B7A]">Please read this waiver in full. You must read it before you can accept it.</p>
            {LIABILITY_WAIVER_TEXT.map((s) => (
              <div key={s.h}>
                <h4 className="text-sm font-bold text-[#1E1E24] mb-1">{s.h}</h4>
                <p className="text-sm text-[#4A4A55] leading-relaxed">{s.p}</p>
              </div>
            ))}
            <p className="text-xs text-[#6B6B7A] pt-2 border-t border-[#F0EDE6]">By clicking the button below you acknowledge that you have read, understood and agree to the terms above on behalf of yourself and all party guests you are responsible for.</p>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-[#F0EDE6]">
            <Button
              onClick={() => { setWaiverRead(true); setLiabilityAccepted(true); setWaiverOpen(false); toast.success('Waiver accepted'); }}
              className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full py-5 text-base font-semibold"
              data-testid="waiver-accept-btn"
            >
              <Check className="w-4 h-4 mr-2" /> I have read and understand the waiver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartyBuilder;
